// ListStepper.js
// Componente re‑escrito con la nueva lógica solicitada

import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import AccountBoxIcon from "@mui/icons-material/AccountBox";

import {
  NO_EES,
  NO_EES_TEMP,
  NO_SUBTEL,
  NO_MINEDUC,
} from "../../Constants/Constants";


const DESCRIPTIONS = {
  DEFAULT: (name) =>
    `Falta validación y firma del representante del ${name}. Las personas encargadas de firmar son:`,
  SUBTEL:
    "Al quedar registrada la firma de SUBTEL el convenio ya está activado y comenzarán a correr los acuerdos. Las personas encargadas de firmar son:",
  MINEDUC:
    "En el caso de haber incluído la firma de un Representante Temporal MINEDUC queda notificado y lo debe validar junto con ingresarlo a sus registros. Las personas encargadas de firmar son:",
};

function createStep(step) {
  switch (step) {
    case "SUBTEL":
      return {
        step,
        label: "Firma SUBTEL",
        description: DESCRIPTIONS.SUBTEL,
      };

    case "EES":
      return {
        step,
        label: "Firma EES",
        description: DESCRIPTIONS.DEFAULT("EES"),
      };

    case "MINEDUC":
      return {
        step,
        label: "Validación MINEDUC",
        description: DESCRIPTIONS.MINEDUC,
      };

    default:
      // paso inicial (FAS, FUS, Contingencia)
      return {
        step,
        label: `Levantamiento de ${step}`,
        description: DESCRIPTIONS.DEFAULT(step),
      };
  }
}


function buildSteps(stepName, formData, hasTemporalSigned, row) {
  const steps = [];

  // 2.1 Paso inicial siempre presente
  steps.push(createStep(stepName));

  const { datos = {} } = formData || {};

  const addStep = (name) => steps.push(createStep(name));
  console.log(formData);
  if (stepName === "FAS" || stepName === "FUS") {
    // FAS / FUS → [stepName, EES, SUBTEL]
    addStep("EES");
    addStep("SUBTEL");
  } else if (stepName.toLowerCase() === "contingencias") {
    const {
      tipo_contingencia: tipo,
      especifica_origen_falla: origen,
    } = datos;


    switch (formData.tipo) {
      case "ZONAL":
        // [stepName, SUBTEL]
        addStep("SUBTEL");
        break;
      case "PROGRAMADA":
        // [stepName, EES]
        addStep("EES");
        break;
      case "ESPECIFICA":
        if (formData.origen === "Externa") {
          addStep("SUBTEL");
        } else {
          addStep("EES");
        }
        break;
      default:
        // fallback: mantener sólo paso inicial
        break;
    }
  }
  // 2.2 Agregar MINEDUC si corresponde
  if (hasTemporalSigned || row.estado === NO_EES_TEMP ) {
    addStep("MINEDUC");
  }

  return steps;
}

/** --------------------------------------------------
 * 3. Componente principal
 * --------------------------------------------------*/
export default function ListStepper({ row, source = "FAS", formData }) {
  // ¿Existe firma temporal ya firmada?
  const temporalSigned = row.firmas?.some(
    (firma) =>
      firma.rol_firma?.includes("Temporal") && firma.estado_firma === "Firmado"
  );

  // Construir pasos iniciales
  const [steps, setSteps] = useState(() =>
    buildSteps(source, formData, temporalSigned, row)
  );

  const [progress, setProgress] = useState(() =>
    steps.reduce((acc, s) => ({ ...acc, [s.step]: 0 }), {})
  );

  const [activeStep, setActiveStep] = useState(-1);


  useEffect(() => {
    const newProgress = { ...progress };
    row.progress?.steps?.forEach(({ institution, type }) => {
      if (newProgress[institution?.type] !== undefined && type !== "Firmado") {
        newProgress[institution.type] = 1;
      }
    });
    setProgress(newProgress);

    let current = 0;

    switch (row.estado) {
      case NO_EES:
      case NO_EES_TEMP:
        current = steps.findIndex((s) => s.step === "EES");
        break;
      case NO_SUBTEL:
        current = steps.findIndex((s) => s.step === "SUBTEL");
        break;
      case NO_MINEDUC:
        current = steps.findIndex((s) => s.step === "MINEDUC");
        break;
      default:
        current = steps.length; // completado
        break;
    }

    setActiveStep(current === -1 ? 0 : current);
  }, [row.estado, row.progress, steps]);


  return (
    <Box sx={{ maxWidth: 400 }}>
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              optional={
                index === steps.length - 1 ? (
                  <Typography variant="caption">Último Paso</Typography>
                ) : null
              }
            >
              {step.label}
            </StepLabel>
            <StepContent>
              <Typography>{step.description}</Typography>

              <List sx={{ width: "100%" }}>
                {(() => {
                  const incomplete = row.progress?.steps?.filter((sp) =>
                    sp.institution?.name?.includes(step.step)
                  );

                  return incomplete?.length
                    ? incomplete.flatMap((sp, i) =>
                        sp.in_charge.map((person, j) => (
                          <ListItem key={`${i}.${j}.item`}>
                            <ListItemAvatar>
                              <Avatar>
                                <AccountBoxIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primaryTypographyProps={{ style: { color: "#111" } }}
                              primary={person}
                              secondary={sp.institution?.rol_firma}
                            />
                          </ListItem>
                        ))
                      )
                    : null;
                })()}
              </List>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {activeStep === steps.length && (
        <Paper square elevation={0} sx={{ p: 3 }}>
          <Typography>Todos los pasos completados.</Typography>
        </Paper>
      )}
    </Box>
  );
}
